// Live meeting conversation: provisional live rows and asking Chat about a meeting while it is
// recorded (PLAN-ADDENDUM A4, PT-008).
//
// Namespace `meetings-live`; owner WP-4 (BUILD 1). Only the owner adds keys or
// translations. Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  liveTranscriptTitle: 'Live transcript',
  provisional: 'Provisional',
  provisionalNote:
    'Live text is provisional. The final transcript comes from the recording after you stop.',
  noLiveTextYet: 'Nothing has been transcribed yet.',
  liveUnavailable:
    'Live text is not available for this recording. The final transcript comes after you stop.',
  liveRowsCount: '{count} live lines so far',
  askAboutMeeting: 'Ask about this meeting',
  askPlaceholder: 'Chat reads the live transcript, so you can ask about what has been said so far.',
  attachedMeeting: 'Meeting attached: {title}',
  stillRecording: 'Still recording',
};

export type MeetingsLiveCopyKey = keyof typeof en;

const meetingsLiveCopy: NamespaceCatalog<MeetingsLiveCopyKey> = {
  bg: {
    liveTranscriptTitle: 'Транскрипция на живо',
    provisional: 'Предварително',
    provisionalNote:
      'Текстът на живо е предварителен. Окончателната транскрипция се прави от записа, след като спрете.',
    noLiveTextYet: 'Все още нищо не е транскрибирано.',
    liveUnavailable:
      'За този запис няма текст на живо. Окончателната транскрипция ще е готова, след като спрете.',
    liveRowsCount: 'Досега {count} реда на живо',
    askAboutMeeting: 'Питайте за тази среща',
    askPlaceholder:
      'Chat чете транскрипцията на живо, така че можете да питате за казаното досега.',
    attachedMeeting: 'Прикачена среща: {title}',
    stillRecording: 'Записът продължава',
  },
  hr: {
    liveTranscriptTitle: 'Transkript uživo',
    provisional: 'Privremeno',
    provisionalNote:
      'Tekst uživo je privremen. Konačni transkript izrađuje se iz snimke nakon zaustavljanja.',
    noLiveTextYet: 'Još ništa nije transkribirano.',
    liveUnavailable:
      'Tekst uživo nije dostupan za ovu snimku. Konačni transkript stiže nakon zaustavljanja.',
    liveRowsCount: 'Dosad {count} redaka uživo',
    askAboutMeeting: 'Pitaj o ovom sastanku',
    askPlaceholder: 'Chat čita transkript uživo pa možete pitati o onome što je dosad rečeno.',
    attachedMeeting: 'Priloženi sastanak: {title}',
    stillRecording: 'Snimanje je u tijeku',
  },
  cs: {
    liveTranscriptTitle: 'Živý přepis',
    provisional: 'Předběžné',
    provisionalNote: 'Živý text je předběžný. Konečný přepis vznikne z nahrávky po zastavení.',
    noLiveTextYet: 'Zatím nebylo nic přepsáno.',
    liveUnavailable:
      'Pro tuto nahrávku není živý text k dispozici. Konečný přepis bude hotový po zastavení.',
    liveRowsCount: 'Zatím {count} živých řádků',
    askAboutMeeting: 'Zeptat se na tuto schůzku',
    askPlaceholder: 'Chat čte živý přepis, takže se můžete zeptat na to, co zatím zaznělo.',
    attachedMeeting: 'Připojená schůzka: {title}',
    stillRecording: 'Stále se nahrává',
  },
  da: {
    liveTranscriptTitle: 'Live-transskription',
    provisional: 'Foreløbig',
    provisionalNote:
      'Liveteksten er foreløbig. Den endelige transskription laves ud fra optagelsen, når du har stoppet.',
    noLiveTextYet: 'Der er ikke transskriberet noget endnu.',
    liveUnavailable:
      'Livetekst er ikke tilgængelig for denne optagelse. Den endelige transskription kommer, når du har stoppet.',
    liveRowsCount: '{count} livelinjer indtil videre',
    askAboutMeeting: 'Spørg om dette møde',
    askPlaceholder:
      'Chat læser live-transskriptionen, så du kan spørge om det, der er blevet sagt indtil nu.',
    attachedMeeting: 'Vedhæftet møde: {title}',
    stillRecording: 'Optager stadig',
  },
  nl: {
    liveTranscriptTitle: 'Livetranscript',
    provisional: 'Voorlopig',
    provisionalNote:
      'De livetekst is voorlopig. Het definitieve transcript wordt na het stoppen uit de opname gemaakt.',
    noLiveTextYet: 'Er is nog niets getranscribeerd.',
    liveUnavailable:
      'Livetekst is niet beschikbaar voor deze opname. Het definitieve transcript volgt na het stoppen.',
    liveRowsCount: 'Tot nu toe {count} liveregels',
    askAboutMeeting: 'Vragen over deze vergadering',
    askPlaceholder:
      'Chat leest het livetranscript mee, dus je kunt vragen stellen over wat er tot nu toe is gezegd.',
    attachedMeeting: 'Bijgevoegde vergadering: {title}',
    stillRecording: 'Opname loopt nog',
  },
  en,
  et: {
    liveTranscriptTitle: 'Reaalajas transkriptsioon',
    provisional: 'Esialgne',
    provisionalNote:
      'Reaalajas tekst on esialgne. Lõplik transkriptsioon tehakse salvestisest pärast peatamist.',
    noLiveTextYet: 'Midagi pole veel transkribeeritud.',
    liveUnavailable:
      'Selle salvestise jaoks pole reaalajas tekst saadaval. Lõplik transkriptsioon tuleb pärast peatamist.',
    liveRowsCount: 'Seni {count} reaalajas rida',
    askAboutMeeting: 'Küsi selle koosoleku kohta',
    askPlaceholder: 'Chat loeb reaalajas transkriptsiooni, nii et saad küsida seni öeldu kohta.',
    attachedMeeting: 'Lisatud koosolek: {title}',
    stillRecording: 'Salvestamine käib',
  },
  fi: {
    liveTranscriptTitle: 'Reaaliaikainen litterointi',
    provisional: 'Alustava',
    provisionalNote:
      'Reaaliaikainen teksti on alustava. Lopullinen litterointi tehdään tallenteesta, kun lopetat.',
    noLiveTextYet: 'Mitään ei ole vielä litteroitu.',
    liveUnavailable:
      'Reaaliaikainen teksti ei ole käytettävissä tälle tallenteelle. Lopullinen litterointi valmistuu, kun lopetat.',
    liveRowsCount: '{count} reaaliaikaista riviä tähän mennessä',
    askAboutMeeting: 'Kysy tästä kokouksesta',
    askPlaceholder:
      'Chat lukee reaaliaikaista litterointia, joten voit kysyä siitä, mitä on tähän mennessä sanottu.',
    attachedMeeting: 'Liitetty kokous: {title}',
    stillRecording: 'Tallennus jatkuu',
  },
  fr: {
    liveTranscriptTitle: 'Transcription en direct',
    provisional: 'Provisoire',
    provisionalNote:
      'Le texte en direct est provisoire. La transcription finale est produite à partir de l’enregistrement une fois arrêté.',
    noLiveTextYet: 'Rien n’a encore été transcrit.',
    liveUnavailable:
      'Le texte en direct n’est pas disponible pour cet enregistrement. La transcription finale arrivera après l’arrêt.',
    liveRowsCount: '{count} lignes en direct jusqu’ici',
    askAboutMeeting: 'Poser une question sur cette réunion',
    askPlaceholder:
      'Chat lit la transcription en direct : vous pouvez poser des questions sur ce qui a été dit jusqu’ici.',
    attachedMeeting: 'Réunion jointe : {title}',
    stillRecording: 'Enregistrement en cours',
  },
  de: {
    liveTranscriptTitle: 'Live-Transkript',
    provisional: 'Vorläufig',
    provisionalNote:
      'Der Live-Text ist vorläufig. Das endgültige Transkript entsteht nach dem Stoppen aus der Aufnahme.',
    noLiveTextYet: 'Bisher wurde nichts transkribiert.',
    liveUnavailable:
      'Für diese Aufnahme ist kein Live-Text verfügbar. Das endgültige Transkript folgt nach dem Stoppen.',
    liveRowsCount: 'Bisher {count} Live-Zeilen',
    askAboutMeeting: 'Zu diesem Meeting fragen',
    askPlaceholder:
      'Der Chat liest das Live-Transkript. Sie können also fragen, was bisher gesagt wurde.',
    attachedMeeting: 'Angehängtes Meeting: {title}',
    stillRecording: 'Aufnahme läuft',
  },
  el: {
    liveTranscriptTitle: 'Ζωντανή απομαγνητοφώνηση',
    provisional: 'Προσωρινό',
    provisionalNote:
      'Το ζωντανό κείμενο είναι προσωρινό. Η τελική απομαγνητοφώνηση γίνεται από την ηχογράφηση αφού σταματήσετε.',
    noLiveTextYet: 'Δεν έχει απομαγνητοφωνηθεί τίποτα ακόμη.',
    liveUnavailable:
      'Το ζωντανό κείμενο δεν είναι διαθέσιμο για αυτή την ηχογράφηση. Η τελική απομαγνητοφώνηση θα έρθει αφού σταματήσετε.',
    liveRowsCount: 'Μέχρι στιγμής {count} ζωντανές γραμμές',
    askAboutMeeting: 'Ρωτήστε για αυτή τη σύσκεψη',
    askPlaceholder:
      'Το Chat διαβάζει τη ζωντανή απομαγνητοφώνηση, οπότε μπορείτε να ρωτήσετε για όσα έχουν ειπωθεί μέχρι τώρα.',
    attachedMeeting: 'Συνημμένη σύσκεψη: {title}',
    stillRecording: 'Η ηχογράφηση συνεχίζεται',
  },
  hu: {
    liveTranscriptTitle: 'Élő átirat',
    provisional: 'Ideiglenes',
    provisionalNote:
      'Az élő szöveg ideiglenes. A végleges átirat a leállítás után a felvételből készül.',
    noLiveTextYet: 'Még semmi sincs átírva.',
    liveUnavailable:
      'Ehhez a felvételhez nem érhető el élő szöveg. A végleges átirat a leállítás után készül el.',
    liveRowsCount: 'Eddig {count} élő sor',
    askAboutMeeting: 'Kérdezz erről a megbeszélésről',
    askPlaceholder: 'A Chat olvassa az élő átiratot, így rákérdezhetsz arra, ami eddig elhangzott.',
    attachedMeeting: 'Csatolt megbeszélés: {title}',
    stillRecording: 'A felvétel folyamatban',
  },
  ga: {
    liveTranscriptTitle: 'Tras-scríbhinn bheo',
    provisional: 'Sealadach',
    provisionalNote:
      'Tá an téacs beo sealadach. Déantar an tras-scríbhinn deiridh ón taifeadadh nuair a stopann tú.',
    noLiveTextYet: 'Níl aon rud tras-scríofa fós.',
    liveUnavailable:
      'Níl téacs beo ar fáil don taifeadadh seo. Tiocfaidh an tras-scríbhinn deiridh nuair a stopann tú.',
    liveRowsCount: '{count} líne bheo go dtí seo',
    askAboutMeeting: 'Fiafraigh faoin gcruinniú seo',
    askPlaceholder:
      'Léann Chat an tras-scríbhinn bheo, mar sin is féidir leat ceist a chur faoina bhfuil ráite go dtí seo.',
    attachedMeeting: 'Cruinniú ceangailte: {title}',
    stillRecording: 'Á thaifeadadh fós',
  },
  it: {
    liveTranscriptTitle: 'Trascrizione dal vivo',
    provisional: 'Provvisorio',
    provisionalNote:
      'Il testo dal vivo è provvisorio. La trascrizione finale viene creata dalla registrazione dopo l’interruzione.',
    noLiveTextYet: 'Non è stato ancora trascritto nulla.',
    liveUnavailable:
      'Il testo dal vivo non è disponibile per questa registrazione. La trascrizione finale arriverà dopo l’interruzione.',
    liveRowsCount: 'Finora {count} righe dal vivo',
    askAboutMeeting: 'Chiedi di questa riunione',
    askPlaceholder:
      'Chat legge la trascrizione dal vivo, quindi puoi chiedere di ciò che è stato detto finora.',
    attachedMeeting: 'Riunione allegata: {title}',
    stillRecording: 'Registrazione in corso',
  },
  lv: {
    liveTranscriptTitle: 'Tiešraides atšifrējums',
    provisional: 'Provizorisks',
    provisionalNote:
      'Tiešraides teksts ir provizorisks. Galīgais atšifrējums tiek veidots no ieraksta pēc apturēšanas.',
    noLiveTextYet: 'Vēl nekas nav atšifrēts.',
    liveUnavailable:
      'Šim ierakstam tiešraides teksts nav pieejams. Galīgais atšifrējums būs pieejams pēc apturēšanas.',
    liveRowsCount: 'Līdz šim {count} tiešraides rindas',
    askAboutMeeting: 'Jautāt par šo sapulci',
    askPlaceholder:
      'Chat lasa tiešraides atšifrējumu, tāpēc varat jautāt par to, kas līdz šim teikts.',
    attachedMeeting: 'Pievienotā sapulce: {title}',
    stillRecording: 'Ierakstīšana turpinās',
  },
  lt: {
    liveTranscriptTitle: 'Tiesioginis nuorašas',
    provisional: 'Preliminarus',
    provisionalNote:
      'Tiesioginis tekstas yra preliminarus. Galutinis nuorašas parengiamas iš įrašo, kai sustabdote.',
    noLiveTextYet: 'Kol kas niekas neperrašyta.',
    liveUnavailable:
      'Šiam įrašui tiesioginis tekstas nepasiekiamas. Galutinis nuorašas bus parengtas, kai sustabdysite.',
    liveRowsCount: 'Kol kas {count} tiesioginės eilutės',
    askAboutMeeting: 'Klausti apie šį susitikimą',
    askPlaceholder:
      'Chat skaito tiesioginį nuorašą, todėl galite klausti apie tai, kas iki šiol buvo pasakyta.',
    attachedMeeting: 'Pridėtas susitikimas: {title}',
    stillRecording: 'Įrašymas tęsiasi',
  },
  mt: {
    liveTranscriptTitle: 'Traskrizzjoni diretta',
    provisional: 'Provviżorju',
    provisionalNote:
      'It-test dirett huwa provviżorju. It-traskrizzjoni finali ssir mir-reġistrazzjoni wara li tieqaf.',
    noLiveTextYet: 'Għadu ma ġie traskritt xejn.',
    liveUnavailable:
      'It-test dirett mhuwiex disponibbli għal din ir-reġistrazzjoni. It-traskrizzjoni finali tasal wara li tieqaf.',
    liveRowsCount: '{count} linji diretti s’issa',
    askAboutMeeting: 'Staqsi dwar din il-laqgħa',
    askPlaceholder:
      'Chat jaqra t-traskrizzjoni diretta, għalhekk tista’ tistaqsi dwar dak li ntqal s’issa.',
    attachedMeeting: 'Laqgħa mehmuża: {title}',
    stillRecording: 'Għadha qed tiġi rrekordjata',
  },
  pl: {
    liveTranscriptTitle: 'Transkrypcja na żywo',
    provisional: 'Wstępne',
    provisionalNote:
      'Tekst na żywo jest wstępny. Ostateczna transkrypcja powstaje z nagrania po jego zatrzymaniu.',
    noLiveTextYet: 'Nic jeszcze nie zostało przetranskrybowane.',
    liveUnavailable:
      'Tekst na żywo nie jest dostępny dla tego nagrania. Ostateczna transkrypcja pojawi się po zatrzymaniu.',
    liveRowsCount: 'Dotychczas wierszy na żywo: {count}',
    askAboutMeeting: 'Zapytaj o to spotkanie',
    askPlaceholder:
      'Chat czyta transkrypcję na żywo, więc możesz zapytać o to, co do tej pory powiedziano.',
    attachedMeeting: 'Dołączone spotkanie: {title}',
    stillRecording: 'Nagrywanie trwa',
  },
  pt: {
    liveTranscriptTitle: 'Transcrição em direto',
    provisional: 'Provisório',
    provisionalNote:
      'O texto em direto é provisório. A transcrição final é feita a partir da gravação quando parar.',
    noLiveTextYet: 'Ainda não foi transcrito nada.',
    liveUnavailable:
      'O texto em direto não está disponível para esta gravação. A transcrição final chega quando parar.',
    liveRowsCount: '{count} linhas em direto até agora',
    askAboutMeeting: 'Perguntar sobre esta reunião',
    askPlaceholder:
      'O Chat lê a transcrição em direto, por isso pode perguntar sobre o que foi dito até agora.',
    attachedMeeting: 'Reunião anexada: {title}',
    stillRecording: 'Ainda a gravar',
  },
  ro: {
    liveTranscriptTitle: 'Transcriere live',
    provisional: 'Provizoriu',
    provisionalNote:
      'Textul live este provizoriu. Transcrierea finală se face din înregistrare după ce te oprești.',
    noLiveTextYet: 'Nu s-a transcris încă nimic.',
    liveUnavailable:
      'Textul live nu este disponibil pentru această înregistrare. Transcrierea finală vine după ce te oprești.',
    liveRowsCount: '{count} rânduri live până acum',
    askAboutMeeting: 'Întreabă despre această ședință',
    askPlaceholder:
      'Chat citește transcrierea live, așa că poți întreba despre ce s-a spus până acum.',
    attachedMeeting: 'Ședință atașată: {title}',
    stillRecording: 'Înregistrarea continuă',
  },
  sk: {
    liveTranscriptTitle: 'Živý prepis',
    provisional: 'Predbežné',
    provisionalNote: 'Živý text je predbežný. Konečný prepis vznikne z nahrávky po zastavení.',
    noLiveTextYet: 'Zatiaľ sa nič neprepísalo.',
    liveUnavailable:
      'Pre túto nahrávku nie je živý text k dispozícii. Konečný prepis bude hotový po zastavení.',
    liveRowsCount: 'Zatiaľ {count} živých riadkov',
    askAboutMeeting: 'Opýtať sa na toto stretnutie',
    askPlaceholder: 'Chat číta živý prepis, takže sa môžete opýtať na to, čo doteraz odznelo.',
    attachedMeeting: 'Pripojené stretnutie: {title}',
    stillRecording: 'Stále sa nahráva',
  },
  sl: {
    liveTranscriptTitle: 'Prepis v živo',
    provisional: 'Začasno',
    provisionalNote:
      'Besedilo v živo je začasno. Končni prepis nastane iz posnetka, ko ustavite snemanje.',
    noLiveTextYet: 'Ničesar še ni bilo prepisano.',
    liveUnavailable:
      'Za ta posnetek besedilo v živo ni na voljo. Končni prepis bo na voljo, ko ustavite snemanje.',
    liveRowsCount: 'Doslej {count} vrstic v živo',
    askAboutMeeting: 'Vprašaj o tem sestanku',
    askPlaceholder:
      'Chat bere prepis v živo, zato lahko vprašate o tem, kar je bilo povedano doslej.',
    attachedMeeting: 'Priložen sestanek: {title}',
    stillRecording: 'Snemanje poteka',
  },
  es: {
    liveTranscriptTitle: 'Transcripción en directo',
    provisional: 'Provisional',
    provisionalNote:
      'El texto en directo es provisional. La transcripción final se crea a partir de la grabación cuando te detienes.',
    noLiveTextYet: 'Todavía no se ha transcrito nada.',
    liveUnavailable:
      'El texto en directo no está disponible para esta grabación. La transcripción final llegará cuando te detengas.',
    liveRowsCount: '{count} líneas en directo hasta ahora',
    askAboutMeeting: 'Preguntar sobre esta reunión',
    askPlaceholder:
      'Chat lee la transcripción en directo, así que puedes preguntar sobre lo que se ha dicho hasta ahora.',
    attachedMeeting: 'Reunión adjunta: {title}',
    stillRecording: 'Sigue grabando',
  },
  sv: {
    liveTranscriptTitle: 'Livetranskript',
    provisional: 'Preliminärt',
    provisionalNote:
      'Livetexten är preliminär. Det slutliga transkriptet görs från inspelningen när du har stoppat.',
    noLiveTextYet: 'Inget har transkriberats än.',
    liveUnavailable:
      'Livetext är inte tillgänglig för den här inspelningen. Det slutliga transkriptet kommer när du har stoppat.',
    liveRowsCount: '{count} liverader hittills',
    askAboutMeeting: 'Fråga om mötet',
    askPlaceholder: 'Chat läser livetranskriptet, så du kan fråga om det som har sagts hittills.',
    attachedMeeting: 'Bifogat möte: {title}',
    stillRecording: 'Spelar fortfarande in',
  },
};
export default meetingsLiveCopy;
